import Lottie from 'lottie-react';
import React, { useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';

import {
  congratulationsRocketPng,
  level1CongratulationsBadgePng,
  level3CongratulationsBadgePng,
} from '../../assets/images';
import { goldStarsLottie } from '../../assets/lottie';
import { SITE_LINKS } from '../../constants';
import { useGetPlacementTestResult } from '../../hooks/use-get-placement-test-result';
import { Background } from '../../stories/Background';
import { Button } from '../../stories/Button';
import { Header } from '../../stories/Header';
import { OnboardingNavbar } from '../../stories/OnboardingNavbar';
import { Tag } from '../../stories/Tag';

const resultData: Record<string, string> = {
  'level1-unit1': 'Starter 1',
  'level1-unit7': 'Level 1',
  'level3-unit1': 'Level 3',
};

export const PlacementTestResultPage: React.FC = () => {
  const __ = useTranslation().t;
  const { placementTestResult, isError, isFetching } = useGetPlacementTestResult();
  const skippedPlacement = placementTestResult?.skippedPlacement || false;
  const levelPosition = placementTestResult?.levelPosition || 1;
  const unitPosition = placementTestResult?.unitPosition || 1;

  const levelUnitText = useMemo(() => {
    return (
      resultData[`level${levelPosition}-unit${unitPosition}`] ||
      placementTestResult?.result ||
      'Starter 1'
    );
  }, [levelPosition, unitPosition, placementTestResult?.result]);

  const badgeSrc = useMemo(() => {
    if (skippedPlacement) { return congratulationsRocketPng; };
    return levelPosition === 3 ? level3CongratulationsBadgePng : level1CongratulationsBadgePng;
  }, [skippedPlacement, levelPosition]);

  const instructionsTitle = skippedPlacement ? __('youAreReadyToBegin') : __('youDidIt');
  const tagTitle = __('completed');
  const btnLabel = __('startLearning');

  const InstructionsDescription = () => {
    if (skippedPlacement) {
      return <span>{__('accessActivitiesAndLiveClasses')}</span>;
    }
    return (
      <>
        <Trans
          alt={__('youAreInLevelUnitStart')}
          i18nKey="youAreInLevelUnitStart"
          values={{ levelUnitText }}
        >
          You are in <strong>{levelUnitText}</strong>. Let`s start!
        </Trans>
      </>
    );
  };
  if (isError) {
    window.location.href = SITE_LINKS.HomePage;
    return null;
  }
  return (
    <>
      <Header type="logo-only" />
      {!isFetching && (
        <Background>
          <Container>
            <div className="oejr-page-onboarding oejr-page-onboarding-5">
              <OnboardingNavbar numberOfCompletedSteps={3} totalSteps={3} />
              <div className="d-flex justify-content-center flex-column align-items-center">
                <div className="oejr-congratulations-wrapper">
                  <div className="d-flex justify-content-center">
                    <Tag shape="vertical-flag" variant="blue-main">
                      {tagTitle}
                    </Tag>
                  </div>
                  <div className="oejr-congratulations-content">
                    <div className="d-flex align-items-center justify-content-center">
                      <img src={badgeSrc} alt="Badge" />
                      {!skippedPlacement && (
                        <Lottie
                          animationData={goldStarsLottie}
                          id="oejr-congratulations-stars-animation"
                        />
                      )}
                    </div>
                    <div className="oejr-assigned-level-instructions">
                      <h1>{instructionsTitle}</h1>
                      <InstructionsDescription />
                    </div>
                  </div>
                </div>
                <Button
                  icon="arrow-right"
                  label={btnLabel}
                  href={SITE_LINKS.LevelDetailsPage(levelPosition)}
                  variant="primary"
                  size="lg"
                />
              </div>
            </div>
          </Container>
        </Background>
      )}
    </>
  );
};
